import { z } from "zod";
import { createRouter, query } from "react-safe-query";

const BASE_URL = "https://api.v3.invintusmedia.com";
const CRUD_URL = "https://crud.v3.invintusmedia.com";

export type EventStatusFromAPI = "new" | "starting" | "live" | "on break" | "available" | "publishing" | "published" | "disconnected" | "stopping";

export interface Event {
  eventID: string;
  clientID: string;
  customID?: string | null;
  startDateTime: string;
  title: string;
  description?: string | null;
  encoderDetails?: {
    simpleName: string;
    online: boolean;
    lastComs: string;
    previewURI: string;
  };
  notes?: string | null;
  eventStatus: EventStatusFromAPI;
  videoThumbnail?: string | null;
  private: boolean;
  autoStart: string;
  autoStop: string;
  estRuntime?: number | null;
  breakOverride?: boolean;
  assignedEncoder?: string | null;
  liveAiCaptions: LiveAiCaptions;
  liveStats?: LiveStats | null;
  streamingURIs: StreamingURIs;
}

export interface MostWatchedEvent {
  eventID: string;
  title: string;
  description: string | null;
  eventStatus: EventStatusFromAPI;
  startDateTime: string;
  image: string | null;
}

export interface StreamingURIs {
  main?: string | null;
  backup?: string | null;
}

interface LiveAiCaptions {
  isEnabled: boolean;
  statsInformation?: null;
}

interface LiveStats {
  encoderID: string;
  liveID: string;
  startDate: string;
  status: string;
  runtime: string;
}

interface PersistentStream {
  encoderID: string;
  title: string;
  description: string;
  inputURI: string;
  streamName: string;
  lastCommunication: string;
  online: boolean;
  live247URI: string;
  thumbImage: string;
}

const getFilteredEventsPayloadSchema = z.object({
  clientID: z.string(),
  categoryID: z.string().optional(),
  anyCategories: z.array(z.string()).optional(),
  includePrivate: z.boolean(),
  eventStatus: z.string().optional(),
  keywords: z.array(z.string()).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

const getEventByEventIDPayloadSchema = z.object({
  clientID: z.string(),
  eventID: z.string(),
  includePrivate: z.boolean(),

  getLiveMetadata: z.boolean().optional(),
  showEncoder: z.boolean().optional(),
  showMediaAssets: z.boolean().optional(),
  showMediaDetails: z.boolean().optional(),
  showStreams: z.boolean().optional(),
});

const getPersistentStreamsPayloadSchema = z.object({
  clientID: z.string(),
  getThumb: z.boolean().optional(),
});

const getMostWatchedEventsPayloadSchema = z.object({
  clientID: z.string()
})

export const eventRouter = createRouter({
  getFilteredEvents: query(async (input: z.infer<typeof getFilteredEventsPayloadSchema>) => {
    const { clientID, anyCategories, includePrivate, eventStatus, keywords, startDate, endDate } = input;

    // Build the request body based on the provided filters
    const requestBody: any = {
      clientID,
      anyCategories,
      includePrivate,
      keywords: keywords && keywords.length > 0 ? keywords.join(", ") : null,
      resultMax: "50",
      showEncoder: false,
      showMediaAssets: false,
      showMediaDetails: false,
      showStreams: false,
      sortBy: "startDateTime asc",
      currentPage: null,
      totalPages: null,
      startDateTime: startDate || "2020-01-01",
      stopDateTime: endDate || new Date().toISOString().split("T")[0],
    };

    if (eventStatus) {
      requestBody.status = eventStatus;
    }

    const response = await fetch(`${BASE_URL}/v2/Console/getEventsSlim`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": "WTw0fEUjXau3J5e79L6iycALE2456sRef9i0vw5kU",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const json = await response.json();
    if (!response.ok) {
      throw new Error(json.message || "Failed to fetch events");
    }

    return json.data as Event[];
  }),
  getEventByEventID: query(async (input: z.infer<typeof getEventByEventIDPayloadSchema>) => {
    const response = await fetch(`${BASE_URL}/v2/Console/getEvent`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": "WTw0fEUjXau3J5e79L6iycALE2456sRef9i0vw5kU",
      },
      body: JSON.stringify(input),
    });

    return (await response.json().then((json) => json.data)) as Event;
  }),
  getPersistentStreams: query(async (input: z.infer<typeof getPersistentStreamsPayloadSchema>) => {
    const response = await fetch(`${BASE_URL}/v2/Feeds/get247Feeds`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": "WTw0fEUjXau3J5e79L6iycALE2456sRef9i0vw5kU",
      },
      body: JSON.stringify(input),
    });

    return (await response.json().then((json) => json.data)) as PersistentStream[];
  }),
  getMostWatchedEvents: query(async (input: z.infer<typeof getMostWatchedEventsPayloadSchema>) => {
    const response = await fetch (`${BASE_URL}/v2/Analytics/getMostWatched`, {
      method: "POST",
      headers: {
        authorization: "console",
        "wsc-api-key": "WTw0fEUjXau3J5e79L6iycALE2456sRef9i0vw5kU",
      },
      body: JSON.stringify(input),
    });

    return (await response.json().then((json) => json.data)) as MostWatchedEvent[]
  })
});
